import Phaser from "phaser";
import type { GameContext } from "../GameContext";
import { LoginController } from "../controllers/LoginController";

/**
 * Per 1.5: "Landing page -> Register -> email/username/password, country,
 * ToS" and "First login -> Character Select". This scene renders a simple
 * DOM-based login/register form (Phaser's DOM Element GameObject) rather
 * than building custom bitmap text inputs — standard practice for
 * text-heavy forms in a Phaser game.
 */
export class LoginScene extends Phaser.Scene {
  private formEl!: HTMLFormElement;
  private statusText!: Phaser.GameObjects.Text;
  private mode: "login" | "register" = "login";

  constructor() {
    super("LoginScene");
  }

  private get ctx(): GameContext {
    return this.game.registry.get("ctx") as GameContext;
  }

  create(): void {
    const { width } = this.cameras.main;

    this.load.svg("logo-full", "/art/logo/aetheria-logo-icon.svg", { width: 64, height: 64 });
    this.load.once("complete", () => {
      this.add.image(width / 2, 62, "logo-full").setOrigin(0.5);
    });
    this.load.start();

    this.add
      .text(width / 2, 110, "AETHERIA", { fontFamily: "Baloo 2, sans-serif", fontSize: "36px", color: "#f5a93f" })
      .setOrigin(0.5);
    this.add
      .text(width / 2, 148, "Chronicles of the Shattered Veil", { fontFamily: "Inter, sans-serif", fontSize: "14px", color: "#a9b4c9" })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(width / 2, 500, "", { fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#e85d4e", wordWrap: { width: 320 } })
      .setOrigin(0.5);

    this.buildForm(width);
  }

  private buildForm(width: number): void {
    const container = document.createElement("form");
    container.innerHTML = this.formHtml();
    this.formEl = container;

    const domElement = this.add.dom(width / 2, 260, container);
    domElement.setOrigin(0.5, 0);

    container.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.handleSubmit();
    });

    container.querySelector("[data-toggle-mode]")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.mode = this.mode === "login" ? "register" : "login";
      this.rebuildFormForMode();
    });
  }

  private formHtml(): string {
    if (this.mode === "login") {
      return `
        <input name="username" placeholder="Username" autocomplete="username" required />
        <input name="password" type="password" placeholder="Password" autocomplete="current-password" required />
        <button type="submit">Enter the Veil</button>
        <a href="#" data-toggle-mode>Need an account? Register</a>
      `;
    }
    return `
      <input name="email" type="email" placeholder="Email" required />
      <input name="username" placeholder="Username" required />
      <input name="password" type="password" placeholder="Password" required />
      <input
        name="country"
        placeholder="US"
        maxlength="2"
        pattern="[A-Za-z]{2}"
        title="2-letter country code, e.g. US, GB, JP (ISO 3166-1 alpha-2)"
        style="text-transform: uppercase"
        required
      />
      <label><input name="acceptedTos" type="checkbox" required /> I accept the Terms of Service</label>
      <button type="submit">Create Account</button>
      <a href="#" data-toggle-mode>Already have an account? Log in</a>
    `;
  }

  private rebuildFormForMode(): void {
    this.formEl.innerHTML = this.formHtml();
    this.statusText.setText("");
    this.formEl.querySelector("[data-toggle-mode]")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.mode = this.mode === "login" ? "register" : "login";
      this.rebuildFormForMode();
    });
  }

  private async handleSubmit(): Promise<void> {
    this.statusText.setText("");
    const formData = new FormData(this.formEl);
    const controller = new LoginController(this.ctx);

    if (this.mode === "login") {
      const result = await controller.login({
        username: String(formData.get("username") ?? ""),
        password: String(formData.get("password") ?? ""),
      });
      if (!result.ok) {
        this.statusText.setText(result.message);
        return;
      }
      this.scene.start("CharacterSelectScene");
      return;
    }

    const result = await controller.register({
      email: String(formData.get("email") ?? ""),
      username: String(formData.get("username") ?? ""),
      password: String(formData.get("password") ?? ""),
      country: String(formData.get("country") ?? "").toUpperCase(),
      acceptedTos: formData.get("acceptedTos") === "on",
    });
    if (!result.ok) {
      this.statusText.setText(result.message);
      return;
    }
    // Registration alone doesn't move StateManager past "unauthenticated"
    // (only login()/authenticating does, per LoginController) — prompt the
    // new account to log in explicitly, matching 1.5's "Register -> First
    // login -> Character Select" flow rather than silently auto-logging in.
    this.mode = "login";
    this.rebuildFormForMode();
    this.statusText.setColor("#7ed957");
    this.statusText.setText("Account created — please log in.");
  }
}
